/* eslint-disable @typescript-eslint/no-explicit-any */
/* v2.3 "Best charts for these fields": looks at what's in a visual's wells
 * (a date? a category with 5 or 50 values? two numbers? an actual and a
 * target?), runs ONE small real query (POST /owner/datasets/{id}/query, the
 * same approved aggregation the visual uses) and draws the 4-6 charts that
 * fit best with the real numbers, each with a plain-words reason. One click
 * switches the visual; bindings move to the new type's wells. */
import { useQuery } from "@tanstack/react-query";
import { Check, Sparkles } from "lucide-react";
import { useMemo } from "react";

import { apiRequest } from "../api/client";
import { CHART_VARIANTS, type ChartVariant } from "./catalog";
import { switchToVariant } from "./ChartTypeGallery";
import type { SWidget } from "./model";

interface FieldMeta { source_name: string; display_name: string; data_type: string; field_role: string }
interface MeasureMeta { name: string; display_name?: string | null; source_field?: string | null }

type Draw = "column" | "column-stacked" | "bar" | "line" | "area" | "combo" | "donut" | "treemap" | "waterfall" | "kpi" | "kpi-trend" | "scatter" | "heatmap" | "bullet" | "gauge" | "column-100";

interface Pick { key: string; label: string; why: string; draw: Draw; score: number; patch: Partial<SWidget>; current: boolean }

const DATE_NAME = /(^|[\s_-])(date|month|year|period|quarter|qtr|week|day|posting)($|[\s_-])/i;
const TARGET_NAME = /(target|budget|plan|goal|forecast|quota)/i;

const variant = (id: string) => CHART_VARIANTS.find((v) => v.id === id) as ChartVariant;

function compact(v: number): string {
  const a = Math.abs(v);
  const t = a >= 1e9 ? `${(a / 1e9).toFixed(1)}B` : a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(a / 1e3).toFixed(1)}K` : a % 1 ? a.toFixed(1) : String(a);
  return (v < 0 ? "-" : "") + t.replace(/\.0([KMB])$/, "$1");
}

export function SmartPicker({ mode, widget, fields, measures, onApply }: { mode: "admin" | "owner"; widget: SWidget; fields: FieldMeta[]; measures: MeasureMeta[]; onApply: (patch: Partial<SWidget>, label: string) => void }) {
  const q = widget.query ?? {};
  const dims: string[] = q.dimensions ?? [];
  const meas: string[] = q.measures ?? [];
  const series: string | null = q.seriesField ?? null;
  const ds = widget.datasetId;
  const queryDims = [...dims, ...(series && !dims.includes(series) ? [series] : [])].slice(0, 2);
  const enabled = !!ds && meas.length > 0;
  const preview = useQuery({
    queryKey: ["smart-preview", mode, ds, queryDims.join("|"), meas.join("|")],
    queryFn: () => apiRequest<{ status: string; data?: { rows?: Array<Record<string, any>> } }>(`/api/v1/owner/datasets/${ds}/query`, { method: "POST", body: { dimensions: queryDims, measures: meas.slice(0, 3), filters: q.filters ?? [], sort: [], limit: 120 } }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  const rows = preview.data?.data?.rows ?? [];
  const fieldOf = (n: string) => fields.find((f) => f.source_name === n);
  const labelOf = (n: string) => fieldOf(n)?.display_name ?? measures.find((m) => m.name === n)?.display_name ?? n;
  const isDate = (n: string) => {
    const f = fieldOf(n);
    return !!f && (f.field_role === "date" || f.data_type === "date" || f.data_type === "datetime" || DATE_NAME.test(` ${f.display_name} `));
  };
  const firstDateField = fields.find((f) => f.field_role === "date" || f.data_type === "date" || f.data_type === "datetime")?.source_name
    ?? fields.find((f) => DATE_NAME.test(` ${f.display_name} `) && f.field_role !== "measure")?.source_name;

  const picks: Pick[] = useMemo(() => {
    if (!enabled) return [];
    const out: Pick[] = [];
    const d0 = dims[0];
    const distinct = d0 ? new Set(rows.map((r) => String(r[d0]))).size : 0;
    const m0 = meas[0];
    const vals = rows.map((r) => Number(r[m0])).filter((n) => Number.isFinite(n));
    const hasNeg = vals.some((v) => v < 0) && vals.some((v) => v > 0);
    const cur = widget.type;
    const chart = widget.display?.chart ?? {};
    const add = (key: string, label: string, why: string, draw: Draw, score: number, patch: Partial<SWidget>, current: boolean) => out.push({ key, label, why, draw, score, patch, current });
    const viaVariant = (id: string, why: string, draw: Draw, score: number) => {
      const v = variant(id);
      const on = v.type === cur && (v.chart.stackMode ?? "none") === ((chart.stackMode as string) || "none") && (v.chart.orientation === "horizontal") === (chart.orientation === "horizontal");
      add(id, v.label, why, draw, score, switchToVariant(widget, v).patch, on);
    };
    const withType = (type: string, extra: Partial<SWidget> = {}) => ({ type, query: { ...q, ...(extra.query ?? {}) }, display: { ...(widget.display ?? {}), chart: { ...(widget.display?.chart ?? {}), stackMode: undefined, orientation: undefined } }, ...Object.fromEntries(Object.entries(extra).filter(([k]) => k !== "query")) });

    if (!d0) {
      viaVariant("kpi", "One headline number", "kpi", 90, );
      viaVariant("gauge", "Against a target or range", "gauge", 60);
      if (firstDateField) add("kpi-trend", "KPI + trend", `Adds a sparkline over ${labelOf(firstDateField)} and the change vs last period`, "kpi-trend", 85, withType("kpi_trend", { query: { ...q, dimensions: [firstDateField], sort: [{ field: firstDateField, direction: "asc" }] } } as any), cur === "kpi_trend");
    } else if (isDate(d0)) {
      viaVariant("line", `Trend over ${labelOf(d0)}`, "line", 95);
      viaVariant("area", "Trend with a sense of volume", "area", 80);
      viaVariant("column", `Compare each ${labelOf(d0).toLowerCase()}`, "column", 75);
      if (meas.length === 1) add("kpi-trend", "KPI + trend", "Big total, sparkline and change vs the previous period", "kpi-trend", 72, withType("kpi_trend", { query: { ...q, dimensions: [d0] } } as any), cur === "kpi_trend");
      if (meas.length >= 2) viaVariant("combo", `${labelOf(meas[0])} as columns, ${labelOf(meas[1])} as a line`, "combo", 88);
      if (hasNeg) viaVariant("waterfall", "Ups and downs build to a total", "waterfall", 70);
      if (dims.length >= 2 || series) viaVariant("area-stacked", "How each part adds up over time", "column-stacked", 78);
    } else {
      if (meas.length >= 2 && TARGET_NAME.test(labelOf(meas[1]))) add("bullet", "Bullet · vs target", `${labelOf(meas[0])} against ${labelOf(meas[1])} on each row`, "bullet", 96, withType("bullet_chart"), cur === "bullet_chart");
      if (distinct > 0 && distinct <= 6 && meas.length === 1 && !hasNeg) viaVariant("donut", `Share of the total across ${distinct} ${labelOf(d0).toLowerCase()} values`, "donut", 90);
      viaVariant(distinct > 8 ? "bar" : "column", distinct > 8 ? `Ranked list — ${distinct} values read best as bars` : `Compare ${labelOf(d0).toLowerCase()} side by side`, distinct > 8 ? "bar" : "column", 92);
      viaVariant(distinct > 8 ? "column" : "bar", distinct > 8 ? "Side by side" : "Long names read better as bars", distinct > 8 ? "column" : "bar", 70);
      if (meas.length === 1 && distinct > 6 && !hasNeg) viaVariant("treemap", `Share across many ${labelOf(d0).toLowerCase()} values`, "treemap", 76);
      if (hasNeg && meas.length === 1) viaVariant("waterfall", "Positives and negatives build up to a total", "waterfall", 82);
      if (meas.length >= 2) {
        viaVariant("combo", `${labelOf(meas[0])} as columns, ${labelOf(meas[1])} as a line`, "combo", 80);
        if (!TARGET_NAME.test(labelOf(meas[1]))) add("bullet", "Bullet · vs target", `Treats ${labelOf(meas[1])} as the target for ${labelOf(meas[0])}`, "bullet", 62, withType("bullet_chart"), cur === "bullet_chart");
        if (distinct >= 6) add("scatter", "Scatter", `Does ${labelOf(meas[1])} rise with ${labelOf(meas[0])}? One dot per ${labelOf(d0).toLowerCase()}`, "scatter", 74, withType("scatter_chart"), cur === "scatter_chart");
      }
      if (dims.length >= 2) {
        add("heatmap", "Heatmap", `${labelOf(dims[0])} × ${labelOf(dims[1])}, coloured by ${labelOf(m0)}`, "heatmap", 86, withType("heatmap_chart", { query: { ...q, dimensions: dims.slice(0, 2), seriesField: null } } as any), cur === "heatmap_chart");
        add("stacked-by", "Stacked column", `Each column split by ${labelOf(dims[1])}`, "column-stacked", 84, { ...switchToVariant(widget, variant("column-stacked")).patch, query: { ...q, dimensions: [dims[0]], seriesField: dims[1] } }, cur === "bar_chart" && chart.stackMode === "stacked");
        add("stacked-100", "100% column", `The ${labelOf(dims[1]).toLowerCase()} mix inside each ${labelOf(dims[0]).toLowerCase()}`, "column-100", 70, { ...switchToVariant(widget, variant("column-100")).patch, query: { ...q, dimensions: [dims[0]], seriesField: dims[1] } }, cur === "bar_chart" && chart.stackMode === "100_percent_stacked");
      }
    }
    const seen = new Set<string>();
    return out
      .filter((p) => (seen.has(p.label) ? false : (seen.add(p.label), true)))
      .sort((a, b) => Number(b.current) - Number(a.current) || b.score - a.score)
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rows, dims.join("|"), meas.join("|"), series, widget.type, JSON.stringify(widget.display?.chart ?? {}), fields.length, measures.length]);

  if (!enabled || !picks.length) return null;
  return (
    <div className="st-smart">
      <div className="st-section__label" style={{ display: "flex", alignItems: "center", gap: 6 }}><Sparkles size={13} color="var(--accent)" /> Best charts for these fields</div>
      {preview.isError ? <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>Couldn't read the numbers for previews — showing shapes only.</div> : null}
      <div className="st-smart__grid">
        {picks.map((p) => (
          <button key={p.key} type="button" className={`st-smart__tile${p.current ? " is-on" : ""}`} aria-pressed={p.current} title={p.why} onClick={() => !p.current && onApply(p.patch, p.label)}>
            <MiniChart draw={p.draw} rows={rows} dims={queryDims} meas={meas} loading={preview.isLoading} />
            <span className="st-smart__label">{p.current ? <Check size={11} /> : null} {p.label}</span>
            <span className="st-smart__why">{p.why}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* Small SVG drawings from the real rows (120 x 64). */
function MiniChart({ draw, rows, dims, meas, loading }: { draw: Draw; rows: Array<Record<string, any>>; dims: string[]; meas: string[]; loading: boolean }) {
  const W = 120, H = 64, P = 4;
  const A = "var(--st-g1, #0f6cbd)", B = "var(--st-g2, #18a8c9)", C = "var(--st-g3, #9fc3e6)";
  const d0 = dims[0], d1 = dims[1], m0 = meas[0], m1 = meas[1];
  const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  // One value per category (summed over a second dimension if present).
  const byCat = new Map<string, number>();
  const byCat2 = new Map<string, number>();
  for (const r of rows) {
    const k = d0 ? String(r[d0]) : "all";
    byCat.set(k, (byCat.get(k) ?? 0) + num(r[m0]));
    if (m1) byCat2.set(k, (byCat2.get(k) ?? 0) + num(r[m1]));
  }
  let cats = [...byCat.keys()];
  const isTime = draw === "line" || draw === "area" || draw === "kpi-trend" || draw === "combo";
  if (isTime) cats.sort();
  else if (draw !== "waterfall") cats.sort((a, b) => (byCat.get(b) ?? 0) - (byCat.get(a) ?? 0));
  cats = cats.slice(0, draw === "bar" || draw === "bullet" ? 5 : 14);
  const v = cats.map((c) => byCat.get(c) ?? 0);
  const v2 = cats.map((c) => byCat2.get(c) ?? 0);
  const maxV = Math.max(1e-9, ...v.map(Math.abs), ...(m1 ? v2.map(Math.abs) : [0]));
  const empty = loading || !rows.length;
  const body = (() => {
    if (empty) return <rect x={P} y={H / 2 - 3} width={W - P * 2} height="6" rx="3" fill={C} opacity="0.5" className={loading ? "st-smart__pulse" : undefined} />;
    const bw = (W - P * 2) / Math.max(1, cats.length);
    switch (draw) {
      case "kpi":
      case "gauge": {
        const total = v.reduce((a, b) => a + b, 0);
        return draw === "gauge" ? (
          <>
            <path d="M24 56 A36 36 0 0 1 96 56" fill="none" stroke={C} strokeWidth="9" />
            <path d="M24 56 A36 36 0 0 1 78 25" fill="none" stroke={A} strokeWidth="9" />
            <text x="60" y="54" fontSize="13" fontWeight="700" textAnchor="middle" fill="currentColor">{compact(total)}</text>
          </>
        ) : <text x="60" y="40" fontSize="22" fontWeight="700" textAnchor="middle" fill="currentColor">{compact(total)}</text>;
      }
      case "kpi-trend": {
        const total = v.reduce((a, b) => a + b, 0);
        const lo = Math.min(...v), hi = Math.max(...v), span = hi - lo || 1;
        const pts = v.map((x, i) => `${P + (i / Math.max(1, v.length - 1)) * (W - P * 2)},${60 - ((x - lo) / span) * 24}`).join(" ");
        return (
          <>
            <text x={P} y="20" fontSize="15" fontWeight="700" fill="currentColor">{compact(total)}</text>
            <polyline points={pts} fill="none" stroke={A} strokeWidth="2" />
          </>
        );
      }
      case "line":
      case "area": {
        const lo = Math.min(0, ...v);
        const y = (x: number) => H - P - ((x - lo) / (maxV - lo || 1)) * (H - P * 2);
        const pts = v.map((x, i) => `${P + (i / Math.max(1, v.length - 1)) * (W - P * 2)},${y(x)}`).join(" ");
        return (
          <>
            {draw === "area" ? <polygon points={`${P},${H - P} ${pts} ${W - P},${H - P}`} fill={A} opacity="0.25" /> : null}
            <polyline points={pts} fill="none" stroke={A} strokeWidth="2" />
          </>
        );
      }
      case "combo": {
        const y2 = (x: number) => H - P - (Math.abs(x) / maxV) * (H - P * 2);
        const pts = v2.map((x, i) => `${P + bw * i + bw / 2},${y2(x)}`).join(" ");
        return (
          <>
            {v.map((x, i) => <rect key={i} x={P + bw * i + bw * 0.18} y={H - P - (Math.abs(x) / maxV) * (H - P * 2)} width={bw * 0.64} height={(Math.abs(x) / maxV) * (H - P * 2)} fill={C} />)}
            <polyline points={pts} fill="none" stroke={A} strokeWidth="2" />
          </>
        );
      }
      case "bar":
      case "bullet": {
        const bh = (H - P * 2) / Math.max(1, cats.length);
        return (
          <>
            {v.map((x, i) => (
              <g key={i}>
                {draw === "bullet" ? <rect x={P} y={P + bh * i + bh * 0.1} width={W - P * 2} height={bh * 0.8} fill={C} opacity="0.45" rx="1" /> : null}
                <rect x={P} y={P + bh * i + bh * (draw === "bullet" ? 0.32 : 0.15)} width={(Math.abs(x) / maxV) * (W - P * 2)} height={bh * (draw === "bullet" ? 0.36 : 0.7)} fill={A} rx="1" />
                {draw === "bullet" && m1 ? <rect x={P + (Math.abs(v2[i]) / maxV) * (W - P * 2) - 1} y={P + bh * i + bh * 0.05} width="2" height={bh * 0.9} fill="currentColor" /> : null}
              </g>
            ))}
          </>
        );
      }
      case "donut": {
        const total = v.reduce((a, b) => a + Math.abs(b), 0) || 1;
        let a0 = -Math.PI / 2;
        const cols = [A, B, C, "#5b8def", "#7cc4d9", "#b8d4ee"];
        return (
          <>
            {v.map((x, i) => {
              const a1 = a0 + (Math.abs(x) / total) * Math.PI * 2;
              const r = 26, cx = 60, cy = 32;
              const large = a1 - a0 > Math.PI ? 1 : 0;
              const path = `M${cx + r * Math.cos(a0)},${cy + r * Math.sin(a0)} A${r},${r} 0 ${large} 1 ${cx + r * Math.cos(a1)},${cy + r * Math.sin(a1)}`;
              a0 = a1;
              return <path key={i} d={path} fill="none" stroke={cols[i % cols.length]} strokeWidth="11" />;
            })}
          </>
        );
      }
      case "treemap": {
        const total = v.reduce((a, b) => a + Math.abs(b), 0) || 1;
        let x = P;
        return <>{v.slice(0, 8).map((val, i) => { const w = (Math.abs(val) / total) * (W - P * 2); const r = <rect key={i} x={x} y={P} width={Math.max(0, w - 1)} height={H - P * 2} fill={i % 2 ? B : A} opacity={1 - i * 0.08} />; x += w; return r; })}</>;
      }
      case "waterfall": {
        let run = 0;
        const steps = v.map((x) => { const s = run; run += x; return [s, run]; });
        const lo = Math.min(0, ...steps.flat()), hi = Math.max(0, ...steps.flat()), span = hi - lo || 1;
        const y = (x: number) => H - P - ((x - lo) / span) * (H - P * 2);
        const wb = (W - P * 2) / (steps.length + 1);
        return (
          <>
            {steps.map(([s, e], i) => <rect key={i} x={P + wb * i + wb * 0.15} y={Math.min(y(s), y(e))} width={wb * 0.7} height={Math.max(1, Math.abs(y(s) - y(e)))} fill={e >= s ? A : "#e5484d"} />)}
            <rect x={P + wb * steps.length + wb * 0.15} y={Math.min(y(0), y(run))} width={wb * 0.7} height={Math.abs(y(0) - y(run))} fill={B} />
          </>
        );
      }
      case "scatter": {
        const xs = rows.map((r) => num(r[m0])), ys = rows.map((r) => num(r[m1]));
        const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
        return <>{rows.slice(0, 60).map((r, i) => <circle key={i} cx={P + ((num(r[m0]) - x0) / (x1 - x0 || 1)) * (W - P * 2)} cy={H - P - ((num(r[m1]) - y0) / (y1 - y0 || 1)) * (H - P * 2)} r="2.4" fill={A} opacity="0.75" />)}</>;
      }
      case "heatmap": {
        const xk = [...new Set(rows.map((r) => String(r[d0])))].slice(0, 10);
        const yk = [...new Set(rows.map((r) => String(r[d1])))].slice(0, 6);
        const cell = new Map(rows.map((r) => [`${r[d0]}|${r[d1]}`, num(r[m0])]));
        const hi = Math.max(1e-9, ...[...cell.values()].map(Math.abs));
        const cw = (W - P * 2) / Math.max(1, xk.length), chh = (H - P * 2) / Math.max(1, yk.length);
        return <>{yk.map((yv, j) => xk.map((xv, i) => <rect key={`${i}-${j}`} x={P + cw * i} y={P + chh * j} width={cw - 1} height={chh - 1} rx="1" fill={A} opacity={0.12 + 0.85 * (Math.abs(cell.get(`${xv}|${yv}`) ?? 0) / hi)} />))}</>;
      }
      case "column-stacked":
      case "column-100":
      case "column":
      default: {
        if ((draw === "column-stacked" || draw === "column-100") && d1) {
          const keys = [...new Set(rows.map((r) => String(r[d1])))].slice(0, 4);
          const stackOf = (c: string) => keys.map((k) => rows.filter((r) => String(r[d0]) === c && String(r[d1]) === k).reduce((a, r) => a + Math.abs(num(r[m0])), 0));
          const stacks = cats.map(stackOf);
          const top = Math.max(1e-9, ...stacks.map((s) => s.reduce((a, b) => a + b, 0)));
          const cols = [A, B, C, "#5b8def"];
          return <>{stacks.map((s, i) => { let y = H - P; const tot = s.reduce((a, b) => a + b, 0) || 1; return s.map((x, k) => { const h = (draw === "column-100" ? x / tot : x / top) * (H - P * 2); y -= h; return <rect key={`${i}-${k}`} x={P + bw * i + bw * 0.15} y={y} width={bw * 0.7} height={h} fill={cols[k]} />; }); })}</>;
        }
        return <>{v.map((x, i) => <rect key={i} x={P + bw * i + bw * 0.15} y={H - P - (Math.abs(x) / maxV) * (H - P * 2)} width={bw * 0.7} height={(Math.abs(x) / maxV) * (H - P * 2)} fill={x < 0 ? "#e5484d" : A} rx="1" />)}</>;
      }
    }
  })();
  return <svg className="st-smart__svg" viewBox={`0 0 ${W} ${H}`} width="100%" height="64" aria-hidden="true">{body}</svg>;
}
